import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { FaPlus, FaTasks, FaFilter, FaSearch, FaTh, FaList, FaChartLine } from 'react-icons/fa';
import TaskKanbanBoard from '../components/Portal/Tasks/TaskKanbanBoard';
import TaskListView from '../components/Portal/Tasks/TaskListView';
import TaskFormModal from '../components/Portal/Tasks/TaskFormModal';
import TaskDetailsModal from '../components/Portal/Tasks/TaskDetailsModal';
import TaskAnalytics from '../components/Portal/Tasks/TaskAnalytics';
import '../styles/Tasks.css';

const Tasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'list', 'analytics'

  // Modals
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    assigned_to: '',
    created_by: '',
    tags: [],
    dueDateFilter: '', // 'overdue', 'today', 'week', 'month'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const [
        statusesRes,
        prioritiesRes,
        tagsRes,
        usersRes
      ] = await Promise.all([
        api.get('/portal/tasks/statuses'),
        api.get('/portal/tasks/priorities'),
        api.get('/portal/tasks/tags/all'),
        api.get('/user-management')
      ]);

      setStatuses(statusesRes.data);
      setPriorities(prioritiesRes.data);
      setTags(tagsRes.data);
      setUsers(usersRes.data.users || usersRes.data);

      await fetchTasks();
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status_id', filters.status);
      if (filters.priority) params.append('priority_id', filters.priority);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      if (filters.created_by) params.append('created_by', filters.created_by);
      if (filters.tags.length > 0) params.append('tags', filters.tags.join(','));
      if (filters.dueDateFilter) params.append('due_date_filter', filters.dueDateFilter);

      const response = await api.get(`/portal/tasks?${params.toString()}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    setShowTaskForm(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowTaskForm(true);
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setShowTaskDetails(true);
  };

  const handleTaskSaved = () => {
    setShowTaskForm(false);
    setSelectedTask(null);
    fetchTasks();
  };

  const handleTaskDeleted = () => {
    setShowTaskDetails(false);
    setSelectedTask(null);
    fetchTasks();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      assigned_to: '',
      created_by: '',
      tags: [],
      dueDateFilter: '',
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== '';
  }).length;

  if (loading) {
    return (
      <div className="tasks-loading">
        <div className="spinner"></div>
        <p>Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      {/* Header */}
      <div className="tasks-header">
        <div className="tasks-header-left">
          <FaTasks className="tasks-header-icon" />
          <div>
            <h1>Task Management</h1>
            <p className="tasks-subtitle">
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'} total
            </p>
          </div>
        </div>
        <div className="tasks-header-right">
          <div className="view-mode-selector">
            <button
              className={`view-mode-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
              title="Kanban Board"
            >
              <FaTh /> Kanban
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <FaList /> List
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'analytics' ? 'active' : ''}`}
              onClick={() => setViewMode('analytics')}
              title="Analytics"
            >
              <FaChartLine /> Analytics
            </button>
          </div>
          <button className="btn-create-task" onClick={handleCreateTask}>
            <FaPlus /> New Task
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tasks-filters">
        <div className="filter-group">
          <FaSearch className="filter-icon" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="filter-search"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {statuses.map(status => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            <option value="">All Priorities</option>
            {priorities.map(priority => (
              <option key={priority.id} value={priority.id}>
                {priority.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.assigned_to}
            onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
            className="filter-select"
          >
            <option value="">All Assignees</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user.email}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.dueDateFilter}
            onChange={(e) => handleFilterChange('dueDateFilter', e.target.value)}
            className="filter-select"
          >
            <option value="">All Due Dates</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due Today</option>
            <option value="week">Due This Week</option>
            <option value="month">Due This Month</option>
          </select>
        </div>

        {activeFiltersCount > 0 && (
          <button onClick={clearFilters} className="btn-clear-filters">
            <FaFilter /> Clear Filters ({activeFiltersCount})
          </button>
        )}
      </div>

      {/* Content */}
      <div className="tasks-content">
        {viewMode === 'kanban' && (
          <TaskKanbanBoard
            tasks={tasks}
            statuses={statuses}
            priorities={priorities}
            users={users}
            onTaskClick={handleViewTask}
            onTaskEdit={handleEditTask}
            onRefresh={fetchTasks}
          />
        )}

        {viewMode === 'list' && (
          <TaskListView
            tasks={tasks}
            statuses={statuses}
            priorities={priorities}
            users={users}
            onTaskClick={handleViewTask}
            onTaskEdit={handleEditTask}
            onRefresh={fetchTasks}
          />
        )}

        {viewMode === 'analytics' && (
          <TaskAnalytics
            tasks={tasks}
            statuses={statuses}
            priorities={priorities}
          />
        )}
      </div>

      {/* Modals */}
      {showTaskForm && (
        <TaskFormModal
          task={selectedTask}
          statuses={statuses}
          priorities={priorities}
          tags={tags}
          users={users}
          onClose={() => setShowTaskForm(false)}
          onSave={handleTaskSaved}
        />
      )}

      {showTaskDetails && selectedTask && (
        <TaskDetailsModal
          taskId={selectedTask.id}
          onClose={() => setShowTaskDetails(false)}
          onEdit={handleEditTask}
          onDeleted={handleTaskDeleted}
          onUpdate={fetchTasks}
        />
      )}
    </div>
  );
};

export default Tasks;
